import React, { type ReactNode, useCallback, useMemo } from "react";

import { TokenProvider } from "../token";
import { UserProvider } from "../user";
import { AxiosProvider } from "../axios";
import { IdentityClientProvider } from "../identity-client";
import { useToken, useUser, useIdentityClient } from "../../hooks";
import { LogoutWarning } from "../../components";
import { decodeJwtToken } from "../../utils";
import { useLocalizationSetup } from "../../hooks/use-localization-setup/use-localization-setup";
import type { ApplicationName } from "../../types";

import { AuthenticationContext, type getLoginDestinationFunc, type SiteLoginResult } from "./authentication.context";

interface InternalAuthenticationProviderProps {
	readonly children: ReactNode;
	readonly applicationName: ApplicationName;
	readonly applicationEnvironment?: string;
	readonly getLoginDestination: getLoginDestinationFunc;
	readonly ssoOnly?: true;
}

export const InternalAuthenticationProvider: React.FC<InternalAuthenticationProviderProps> = ({
	children,
	applicationName,
	applicationEnvironment,
	ssoOnly,
	getLoginDestination,
}) => {
	useLocalizationSetup();

	const { client, createClient } = useIdentityClient();

	const { clearToken, setToken } = useToken();
	const { currentUser, setUser } = useUser();

	const authenticated = currentUser === undefined ? undefined : Boolean(currentUser);

	const loginToSite = useCallback(
		async (tenantId: string): Promise<SiteLoginResult | null> => {
			const siteTokenResponse = await client.getSite(tenantId);

			if (siteTokenResponse.success === false || !siteTokenResponse.data) {
				return null;
			}

			const siteToken = siteTokenResponse.data.access_token;

			const siteClient = createClient(siteToken);
			const userResponse = await siteClient.hydrateUser();

			if (userResponse.success === false || !userResponse.data) {
				return null;
			}

			setToken(siteToken);
			setUser(userResponse.data);

			return {
				user: userResponse.data,
				decodedToken: decodeJwtToken(siteToken),
			};
		},
		[client, createClient, setToken, setUser],
	);

	const value = useMemo(
		() => ({
			authenticated,
			applicationName,
			applicationEnvironment,
			ssoOnly,
			logout: clearToken,
			loginToSite,
			getLoginDestination,
		}),
		[authenticated, applicationName, applicationEnvironment, ssoOnly, clearToken, loginToSite, getLoginDestination],
	);

	return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

interface AuthenticationProviderProps {
	readonly identityBaseRoute: string;
	readonly applicationName: ApplicationName;
	readonly applicationEnvironment?: string;
	readonly children: ReactNode;
	readonly getLoginDestination: getLoginDestinationFunc;
	readonly storagePrefix?: string;
	readonly ssoOnly?: true;
}

export const AuthenticationProvider: React.FC<AuthenticationProviderProps> = ({
	children,
	identityBaseRoute,
	applicationName,
	applicationEnvironment,
	getLoginDestination,
	storagePrefix,
	ssoOnly,
}) => {
	return (
		<TokenProvider storagePrefix={storagePrefix || ""} identityBaseRoute={identityBaseRoute}>
			<IdentityClientProvider identityBaseRoute={identityBaseRoute}>
				<UserProvider>
					<InternalAuthenticationProvider
						applicationName={applicationName}
						applicationEnvironment={applicationEnvironment}
						getLoginDestination={getLoginDestination}
						ssoOnly={ssoOnly}
					>
						<AxiosProvider>
							{children}
							<LogoutWarning />
						</AxiosProvider>
					</InternalAuthenticationProvider>
				</UserProvider>
			</IdentityClientProvider>
		</TokenProvider>
	);
};
